import React, { useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import classnames from 'classnames';

import { useCountry, useTranslation } from '@lt/localization';
import { useForceRender } from 'src/hooks/useForceRender';

// Helpers:
import { isSSR } from 'src/helpers/ssr';

// Components:
import { HotelCounter } from 'src/components/HotelCounter';
import HintFactory from 'src/components/Hint/HintFactory';
import SubscribeMobileMountains from 'src/components/SubscribeMobileMountains';
import Footer from 'src/components/Footer';
import styles from './mainContainer.module.css';
import EmptyHotels from '../Hotels/components/EmptyHotels';
import NearestOffers from '../EmptySearch/NearestOffers';
import MapContainer from '../Map';
import Subscribe from '../SubscriptionForm';
import { ModifierBanner } from '../ModifierBanner';

// Context:
import { MobileContext } from '../../domain/context/MobileContext';
import { LanguageContext } from '../../domain/context/LanguageContext';

// Containers:
import { HeaderContainer } from '../Header';
import { RecommendationContainer } from '../Recommendation';
import FilterContainer from '../FiltersSection';
import Sorting from '../Sorting';
import HotelsContainer from '../Hotels';
import { DepartureContainer } from '../DepartureSection';
import LinksSection from '../LinksSection';
import { PageDescription } from '../PageDescription';
import FloatingPanelMobile from '../FloatingPanelMobile';

import type { MainContainerProps } from './types';

export const MainContainer = (props: MainContainerProps): JSX.Element => {
  const {
    className = '',
    language = 'ru',
    is_mobile = false,
    fallbackSearch = false,
    hotelCounter,
    mapExpanded,
    hasHotels,
    nearestDeparturesData,
    nearestDatesData,
    modifierStyles,
    modifierBanner,
    searchType,
    isMetaregion,
    allowShowSubscription,
    hideFilters,
    isSubscribeShown,
    isOpenedMapPopup,
    isWhitelabel,
  } = props;
  const isMobile = Boolean(is_mobile);
  const { t } = useTranslation();
  const { isRussia } = useCountry();
  const ref = useRef<HTMLDivElement>(null);
  const { countForceRender } = useForceRender();

  const mapContainerMarginTop =
    (!isMobile &&
      (nearestDeparturesData || nearestDatesData || isMetaregion) &&
      '0') ||
    (!isMobile && fallbackSearch && '66') ||
    undefined;

  const showSubscribeWidget = allowShowSubscription && isRussia;
  const showEmptySection =
    fallbackSearch && !nearestDeparturesData && !nearestDatesData;
  const showNearestOffers = !!nearestDeparturesData || !!nearestDatesData;
  const showFloatingPanel =
    isMobile &&
    !isSubscribeShown &&
    hideFilters &&
    !showEmptySection &&
    !isOpenedMapPopup;

  const [visibleHeight, setVisibleHeight] = useState(0);

  return (
    <MobileContext.Provider value={isMobile}>
      <LanguageContext.Provider value={language}>
        <div
          className={classnames(styles.container, {
            [styles.containerMobile]: isMobile,
            [styles.containerBackgroundColor]: modifierStyles?.backgroundColor,
          })}
        >
          <HintFactory isMobile={isMobile} events class="react-hint" />

          <HeaderContainer setVisibleHeight={setVisibleHeight} />

          <div
            className={classnames(className, styles.wrapper, {
              [styles.wrapperBackgroundColor]: modifierStyles?.backgroundColor,
            })}
          >
            {modifierBanner ? (
              <ModifierBanner content={modifierBanner} />
            ) : null}

            <RecommendationContainer />

            <div
              className={classnames(styles.mainSection, {
                [styles.mainSectionMobile]: isMobile,
              })}
              ref={ref}
            >
              {showFloatingPanel && (
                <SubscribeMobileMountains>
                  <FloatingPanelMobile />
                </SubscribeMobileMountains>
              )}

              <StickyBox offsetTop={70} className={styles.stickyBox}>
                <div
                  className={classnames(styles.sidebar, {
                    [styles.sidebarMobile]: isMobile,
                  })}
                >
                  {showSubscribeWidget && (
                    <div
                      className={classnames({
                        [styles.subscribeWrapperDesktop]: !isMobile,
                      })}
                    >
                      <Subscribe />
                    </div>
                  )}
                  <FilterContainer />

                  <DepartureContainer />
                </div>
              </StickyBox>

              <div
                className={classnames(styles.centerSection, {
                  [styles.centerSectionMobile]: isMobile,
                })}
              >
                {showEmptySection ? (
                  <div
                    className={classnames({
                      [styles.emptySectionDesktop]: !isMobile,
                      [styles.emptySectionMapExpanded]: mapExpanded,
                    })}
                  >
                    <div />

                    <EmptyHotels isMobile={isMobile} />
                  </div>
                ) : null}

                <div
                  className={classnames({
                    [styles.secondSectionDesktop]: !isMobile,
                  })}
                >
                  <div
                    className={classnames(styles.hotelSection, {
                      [styles.hotelSectionDesktop]: !isMobile,
                      [styles.hotelSectionMapExpanded]: mapExpanded,
                    })}
                  >
                    {showNearestOffers && (
                      <NearestOffers
                        className={classnames(styles.nearestOffers, {
                          [styles.nearestOffersMobile]: isMobile,
                        })}
                        nearestDeparturesData={nearestDeparturesData}
                        nearestDatesData={nearestDatesData}
                        isMobile={isMobile}
                      />
                    )}
                    {isMetaregion && (
                      <LinksSection
                        isMetaregion
                        variant="countryList"
                        isMobile={isMobile}
                      />
                    )}

                    <div className={styles.sortingSection}>
                      {!hasHotels || fallbackSearch ? null : (
                        <>
                          <Sorting />
                          {isMobile ? null : (
                            <HotelCounter
                              hotelCounter={hotelCounter}
                              modifierStyles={modifierStyles}
                              searchType={searchType}
                            />
                          )}
                        </>
                      )}
                    </div>

                    {fallbackSearch ? (
                      <div
                        className={classnames(styles.title, {
                          [styles.titleMobile]: isMobile,
                        })}
                      >
                        {t('explore.hotels.alternatives')}
                      </div>
                    ) : null}

                    <HotelsContainer />

                    <LinksSection
                      variant={isMetaregion ? 'resortList' : 'default'}
                      isMobile={isMobile}
                    />

                    <PageDescription />
                  </div>

                  {isSSR ? null : (
                    <div className={styles.explorePageMap}>
                      <MapContainer
                        marginTop={mapContainerMarginTop}
                        visibleHeight={visibleHeight}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {!isWhitelabel && <Footer key={countForceRender} />}
        </div>
      </LanguageContext.Provider>
    </MobileContext.Provider>
  );
};
